import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Spin,
  message,
} from "antd";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";


import api from "../../../../api/api";
import { pdf } from "@react-pdf/renderer";
import ProfitGeneratePdf from "./ProfitGeneratePdf";

const { Title } = Typography;

const ProfitAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalProfit, setTotalProfit] = useState(0);

  // FETCH DATA
  const fetchProfitData = async () => {
    try {
      setLoading(true);

      const res = await api.get("/profit");

      setData(res.data.products || []);
      setTotalProfit(res.data.total_profit || 0);
    } catch (err) {
      message.error("Failed to load profit data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitData();
  }, []);

  // FORMAT FOR CHARTS
  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.name,
      profit: Number(item.total_profit),
      quantity: item.quantity_sold,
    }));
  }, [data]);

  // DOWNLOAD PDF
  const handleDownloadPDF = async () => {
    try {
      const blob = await pdf(
        <ProfitGeneratePdf data={data} totalProfit={totalProfit} />
      ).toBlob();

      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "profit-report.pdf";
      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      message.error("PDF generation failed");
    }
  };

  if (loading) {
    return (
      <Spin size="large" style={{ display: "flex", justifyContent: "center", marginTop: 50 }} />
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <Title level={3}>Profit Analytics Dashboard</Title>

      {/* SUMMARY */}
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card>
            <Title level={5}>Total Products</Title>
            <h2>{data.length}</h2>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card>
            <Title level={5}>Total Profit</Title>
            <h2>${Number(totalProfit).toFixed(2)}</h2>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card>
            <Button type="primary" onClick={handleDownloadPDF} block>
              Download Profit PDF
            </Button>
          </Card>
        </Col>
      </Row>

      {/* CHARTS */}
      <Row gutter={16} style={{ marginTop: 20 }}>
        <Col xs={24} lg={12}>
          <Card title="Profit per Product (Bar Chart)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="profit" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Profit Trend (Line Chart)">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" hide />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="profit" stroke="#52c41a" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* TABLE STYLE SUMMARY */}
      <Card style={{ marginTop: 20 }} title="Profit Breakdown">
        {data.map((item) => (
          <Row key={item.id} style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
            <Col span={6}>{item.name}</Col>
            <Col span={6}>Sold: {item.quantity_sold}</Col>
            <Col span={6}>Unit Profit: ${item.profit_per_unit}</Col>
            <Col span={6}>
              <b>Total: ${item.total_profit}</b>
            </Col>
          </Row>
        ))}
      </Card>
    </div>
  );
};

export default ProfitAnalytics;